import { GridSortItem } from "@mui/x-data-grid";
import { useState } from "react";
import { useParams } from "react-router-dom";
import CustomDataGridV2 from "../../../../../../../components/CustomDataGridV2";
import RenderWithTooltip from "../../../../../../../components/RenderWithTooltip";
import ActionMenu from "../../../../../../../components/app/ActionMenu";
import DeleteModal, {
  useDeleteDisClosure,
} from "../../../../../../../components/app/DeleteModal";
import { useRoleController } from "../../../../../../../components/useRoleController";
import useDisclosure from "../../../../../../../hooks/use-disclosure";
import { useArchivePatientSurgicalHistory } from "../../../../../../../queries/history/past-surgical/use-past-surgical-history-archive";
import { usePastSurgicalHistoryList } from "../../../../../../../queries/history/past-surgical/use-past-surgical-history-list";
import { SurgicalHistory } from "../../../../../../../services";
import { formatDate } from "../../../../../../../utils/date-time-utility";
import {
  DEF_PAGINATION,
  DGT_Pagination,
  applyDefaultColumnSettings,
  filterActionCol,
  roleFilter,
} from "../../../../../../../utils/utils";
import UpsertSurgicalHistoryDrawer from "./UpsertSurgicalHistoryDrawer";
import { getStoredRealm } from "@/utils/auth-storage";

const PastSurgicalHistoryNew = () => {
  const isVisitNotePage = window.location.pathname.includes("/visit-note");
  const [pagination, setPagination] = useState<DGT_Pagination>(DEF_PAGINATION);
  const [gridSorting, setGridSorting] = useState<GridSortItem[]>([]);
  const { patientId } = useParams() as { patientId: any };
  const { mutateAsync: archiveSurgicalHistory } =
    useArchivePatientSurgicalHistory();
  const surgicalDisclosure = useDisclosure(false);

  const {
    data: surgicalList,
    refetch,
    isFetching,
  } = usePastSurgicalHistoryList({
    patientUuid: patientId,
    pagination,
    gridSorting,
    archive: false,
    xTENANTID: getStoredRealm() ?? undefined,
  });
  const { hasUpdate, hasDelete } = useRoleController("Surgical History");

  const deletePastSurgicalDisclosure = useDeleteDisClosure();
  const COLUMNS = applyDefaultColumnSettings<SurgicalHistory>(
    [
      {
        field: "surgeryName",
        headerName: "Surgery",
        flex: 0.35,
        minWidth: 120,
        renderCell: ({ value }) => <RenderWithTooltip value={value} />,
      },
      {
        field: "surgery_date",
        headerName: "Surgery Date",
        flex: 0.25,
        minWidth: 100,
        renderCell: ({ row }) =>
          row?.surgeryDate ? formatDate(row?.surgeryDate) : "-",
      },
      {
        field: "note",
        headerName: "Note",
        flex: 0.6,
        minWidth: 150,
        renderCell: ({ value }) => <RenderWithTooltip value={value} />,
      },
      {
        field: "recordedDate",
        headerName: "Recorded Date",
        flex: 0.25,
        minWidth: 100,
        renderCell: ({ row }) =>
          row?.recordedDate ? formatDate(row?.recordedDate) : "-",
      },
      {
        field: "Action",
        headerName: "Actions",
        width: 70,
        align: "center",
        headerAlign: "center",
        hideSortIcons: true,
        renderCell: ({ row }) => {
          return (
            <ActionMenu
              options={roleFilter({
                list: [
                  { value: "edit", label: "Edit" },
                  { value: "delete", label: "Delete" },
                ],
                compWith: {
                  edit: hasUpdate,
                  delete: hasDelete,
                },
              })}
              onActionClick={(v) => {
                if (v === "edit") {
                  surgicalDisclosure.open(row);
                } else deletePastSurgicalDisclosure.open(row?.uuid);
              }}
            />
          );
        },
      },
    ],
    ["surgery_date", "recordedDate"]
  );

  function archiveHandler<T>(value: boolean, id: T) {
    return archiveSurgicalHistory({
      patientSurgicalHistoryId: id?.toString() ?? "",
      status: value,
      xTENANTID: getStoredRealm() ?? undefined,
    }).then((res) => {
      refetch();
      return res;
    });
  }

  return (
    <>
      <UpsertSurgicalHistoryDrawer disclosureHost={surgicalDisclosure} />

      <DeleteModal
        w="380px"
        open={deletePastSurgicalDisclosure.opened}
        onClose={deletePastSurgicalDisclosure.close}
        id={deletePastSurgicalDisclosure.deleteId}
        onConfirmation={(id) => archiveHandler(true, id)}
        content={"You want to delete this Past Surgical History"}
      />

      {/* <ChartTableWrapper> */}
      <CustomDataGridV2
        getRowId={(row) => row?.uuid}
        loading={isFetching}
        rows={surgicalList?.data?.content ?? []}
        rowCount={surgicalList?.data?.page?.totalElements ?? 10}
        columns={filterActionCol(COLUMNS, isVisitNotePage)}
        // sx={{ marginTop: "40px" }}
        disableRowSelectionOnClick
        paginationModel={pagination}
        onPaginationModelChange={setPagination}
        onSortModelChange={setGridSorting}
        sortModel={gridSorting}
      />
      {/* </ChartTableWrapper> */}
    </>
  );
};

export default PastSurgicalHistoryNew;
